import { useEffect, useMemo, useState } from 'react'
import { Alert, Card, DatePicker, Segmented, Select, Space, Spin, Statistic, Tag, Typography, theme } from 'antd'
import type { GlobalToken } from 'antd'
import { useQuery } from '@tanstack/react-query'
import { type Dayjs } from 'dayjs'
import { useTranslation } from 'react-i18next'
import { translateError } from '@/shared/api/errors'
import { queryKeys } from '@/shared/api/queryKeys'
import { usePlanOptions, useVispOptions } from '@/shared/api/reference'
import { usePermissions } from '@/shared/auth/usePermissions'
import { tenantToday } from '@/shared/lib/dayRange'
import { formatDate, formatDateTime } from '@/shared/lib/format'
import { formatMoneyValue } from '@/shared/lib/money'
import { displayName } from '@/shared/lib/planName'
import { PageHeader } from '@/shared/ui/PageHeader'
import { fetchAllUsers } from '@/features/identity/identityApi'
import { SubscriberPicker } from '@/features/cashdesk/SubscriberPicker'
import { useBranchOptions } from '@/features/reports/useBranchOptions'
import { useActiveBranches } from '@/features/branch/useActiveBranches'
import {
  DASHBOARD_METRICS,
  ISO_DAY,
  fetchFinanceDashboard,
  ignoredFilters,
  periodRange,
  type DashboardFilter,
  type DashboardMetric,
  type DashboardPeriod,
} from '@/features/reports/financeDashboardApi'

const { RangePicker } = DatePicker

const METRIC_ACCENT: Record<DashboardMetric, (token: GlobalToken) => string> = {
  totalSales: (token) => token.blue,
  totalCollected: (token) => token.colorSuccess,
  totalDiscounts: (token) => token.colorWarning,
  outstanding: (token) => token.colorError,
  cashCollected: (token) => token.cyan,
}


export function FinanceDashboardPage() {
  const { t, i18n } = useTranslation()
  const { can } = usePermissions()
  const { token } = theme.useToken()
  const [period, setPeriod] = useState<DashboardPeriod>('all')
  const [custom, setCustom] = useState<[Dayjs, Dayjs]>(() => [tenantToday().subtract(6, 'day'), tenantToday()])
  const { selected: activeBranches } = useActiveBranches()
  const [branchIds, setBranchIds] = useState<string[]>(() => [...activeBranches])
  const activeKey = activeBranches.join()
  useEffect(() => {
    setBranchIds(activeKey ? activeKey.split(',') : [])
  }, [activeKey])
  const [centerId, setCenterId] = useState<string | null>(null)
  const [vispId, setVispId] = useState<string | null>(null)
  const [cashierId, setCashierId] = useState<string | null>(null)
  const [subscriberId, setSubscriberId] = useState<string | null>(null)
  const [planId, setPlanId] = useState<string | null>(null)

  const range = periodRange(period, custom)
  const from = range ? range[0].format(ISO_DAY) : null
  const to = range ? range[1].format(ISO_DAY) : null
  const filter: DashboardFilter = useMemo(
    () => ({
      from,
      to,
      branchIds,
      centerId,
      vispId,
      cashierId,
      subscriberId,
      planId,
    }),
    [from, to, branchIds, centerId, vispId, cashierId, subscriberId, planId],
  )

  const dashboard = useQuery({
    queryKey: queryKeys.financeDashboard(filter),
    queryFn: ({ signal }) => fetchFinanceDashboard(filter, signal),
  })

  const branches = useBranchOptions()
  const visps = useVispOptions(can('network.visp.read'))
  const centers = useMemo(() => {
    const seen = new Map<string, string>()
    for (const visp of visps) seen.set(visp.centerId, visp.centerName)
    return [...seen.entries()].map(([value, label]) => ({ value, label }))
  }, [visps])
  const plans = usePlanOptions(can('plan.read'))
  const users = useQuery({
    queryKey: queryKeys.identity.users,
    queryFn: fetchAllUsers,
    enabled: can('user.read'),
    staleTime: 5 * 60_000,
  })

  const data = dashboard.data

  return (
    <>
      <PageHeader title={t('nav.financeDashboard')} description={t('pageIntro.financeDashboard')} />

      <Card style={{ marginBottom: 16 }}>
        <Space wrap>
          <Segmented<DashboardPeriod>
            value={period}
            onChange={setPeriod}
            options={[
              { value: 'all', label: t('financeDashboard.period.all') },
              { value: 'today', label: t('financeDashboard.period.today') },
              { value: 'month', label: t('financeDashboard.period.month') },
              { value: 'custom', label: t('financeDashboard.period.custom') },
            ]}
          />
          {period === 'custom' ? (
            <RangePicker
              value={custom}
              allowClear={false}
              disabledDate={(day) => day.isAfter(tenantToday(), 'day')}
              onChange={(value) => {
                if (value?.[0] && value[1]) setCustom([value[0], value[1]])
              }}
            />
          ) : null}
          {branches.length > 0 ? (
            <Select<string[]>
              mode="multiple"
              allowClear
              maxTagCount="responsive"
              optionFilterProp="label"
              style={{ minWidth: 240 }}
              placeholder={t('financeDashboard.filter.allBranches')}
              value={branchIds}
              onChange={setBranchIds}
              options={branches}
            />
          ) : null}
          {centers.length > 0 ? (
            <Select<string>
              allowClear
              showSearch
              optionFilterProp="label"
              style={{ minWidth: 180 }}
              placeholder={t('financeDashboard.filter.center')}
              value={centerId ?? undefined}
              onChange={(value) => {
                setCenterId(value ?? null)
                setVispId(null)
              }}
              options={centers}
            />
          ) : null}
          {visps.length > 0 ? (
            <Select<string>
              allowClear
              showSearch
              optionFilterProp="label"
              style={{ minWidth: 180 }}
              placeholder={t('financeDashboard.filter.visp')}
              value={vispId ?? undefined}
              onChange={(value) => setVispId(value ?? null)}
              options={visps
                .filter((visp) => centerId === null || visp.centerId === centerId)
                .map((visp) => ({ value: visp.id, label: visp.name }))}
            />
          ) : null}
          {can('user.read') ? (
            <Select<string>
              allowClear
              showSearch
              optionFilterProp="label"
              style={{ minWidth: 200 }}
              loading={users.isPending}
              placeholder={t('financeDashboard.filter.cashier')}
              value={cashierId ?? undefined}
              onChange={(value) => setCashierId(value ?? null)}
              options={(users.data ?? []).map((user) => ({
                value: user.id,
                label: user.displayName || user.username,
              }))}
            />
          ) : null}
          {can('subscriber.directory') ? (
            <SubscriberPicker
              value={subscriberId}
              onChange={setSubscriberId}
              placeholder={t('financeDashboard.filter.customer')}
            />
          ) : null}
          {plans.length > 0 ? (
            <Select<string>
              allowClear
              showSearch
              optionFilterProp="label"
              style={{ minWidth: 200 }}
              placeholder={t('financeDashboard.filter.package')}
              value={planId ?? undefined}
              onChange={(value) => setPlanId(value ?? null)}
              options={plans.map((plan) => ({ value: plan.id, label: displayName(plan, i18n.language) }))}
            />
          ) : null}
        </Space>
      </Card>

      {dashboard.isError ? (
        <Alert type="error" showIcon style={{ marginBottom: 16 }} message={translateError(dashboard.error, t)} />
      ) : null}
      {data?.restricted ? (
        <Alert type="info" showIcon style={{ marginBottom: 16 }} message={t('financeDashboard.restricted')} />
      ) : null}

      <Spin spinning={dashboard.isFetching && !dashboard.isPending}>
        <div style={{ display: 'grid', gap: 16, gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))' }}>
          {DASHBOARD_METRICS.map((metric) => {
            const ignored = data ? ignoredFilters(data, metric, filter) : []
            return (
              <div key={metric}>
                <Card
                  loading={dashboard.isPending}
                  style={{ height: '100%', borderTop: `3px solid ${METRIC_ACCENT[metric](token)}` }}
                >
                  <Statistic
                    title={t(`financeDashboard.metric.${metric}`)}
                    value={data ? formatMoneyValue(data.metrics[metric]) : ''}
                    valueStyle={{ fontSize: token.fontSizeHeading4 }}
                  />
                  <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                    {t(`financeDashboard.hint.${metric}`)}
                  </Typography.Text>
                  {ignored.length > 0 ? (
                    <div style={{ marginTop: 8 }}>
                      <Tag color="default">
                        {t('financeDashboard.notApplied', {
                          filters: ignored.map((key) => t(`financeDashboard.filterName.${key}`)).join(', '),
                        })}
                      </Tag>
                    </div>
                  ) : null}
                </Card>
              </div>
            )
          })}
        </div>
      </Spin>

      {data ? (
        <Typography.Paragraph type="secondary" style={{ marginTop: 16 }}>
          {data.from
            ? t('financeDashboard.asOf', { at: formatDateTime(data.asOf), from: formatDate(data.from), to: formatDate(data.to) })
            : t('financeDashboard.asOfAllTime', { at: formatDateTime(data.asOf), to: formatDate(data.to) })}
        </Typography.Paragraph>
      ) : null}
    </>
  )
}
